import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { getLastUserSearches, setLastUserSearch, type UserSearch } from './utils'

interface ISuggesterSearchType {
  userLastSearches: UserSearch[]
  addUserLastSearch: (userLastSearches: UserSearch) => void
  removeUserSearch: (id: string) => void
}

const MAX_SEARCHES = 3

export const SuggesterSearchContext = createContext<ISuggesterSearchType | null>(null)

interface ISuggesterSearchProviderType {
  children: React.ReactNode
}

export const SuggesterSearchProvider = ({ children }: ISuggesterSearchProviderType) => {
  const [userLastSearches, setUserLastSearches] = useState<UserSearch[]>([])

  useEffect(() => {
    setUserLastSearches(getLastUserSearches())
  }, [])

  const addUserLastSearch = useCallback(
    (newSearch: UserSearch) => {
      setUserLastSearches(prevSearches => {
        const searchIndex = userLastSearches.findIndex(
          (search: UserSearch) =>
            (search.location.country === newSearch.location.country &&
              search.location.type === newSearch.location.type &&
              search.location.slug === newSearch.location.slug) ||
            search.location.id === newSearch.location.id
        )

        if (searchIndex !== -1) {
          const updatedSearches = [
            ...prevSearches.slice(0, searchIndex),
            newSearch,
            ...prevSearches.slice(searchIndex + 1),
          ]
          setLastUserSearch(updatedSearches)
          return updatedSearches
        }
        const newSearches = [newSearch, ...prevSearches].slice(0, MAX_SEARCHES)
        setLastUserSearch(newSearches)
        return newSearches
      })
    },
    [userLastSearches]
  )

  const removeUserSearch = useCallback((searchLocationId: string) => {
    setUserLastSearches(prevSearches => {
      const newSearches = prevSearches.filter(search => search.location.id !== searchLocationId)
      setLastUserSearch(newSearches)
      return newSearches
    })
  }, [])

  const value = useMemo(
    () => ({ userLastSearches, addUserLastSearch, removeUserSearch }),
    [userLastSearches, addUserLastSearch, removeUserSearch]
  )

  return <SuggesterSearchContext.Provider value={value}>{children}</SuggesterSearchContext.Provider>
}
